import { formatDate, isAfter } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import {
  StorefrontCalendarFeedDocument,
  useClosuresCreatePerProductMutation,
  useProductClosureByIdQuery,
  useClosuresUpdatePerProductMutation,
  ClosuresUpdatePerProductMutationVariables,
} from "gql/generated";
import _ from "lodash";
import { useCallback } from "react";
import { useFormHandler } from "shared/hooks";
import { DATE_PATTERN_SIMPLE } from "shared/utils";
import { z } from "zod";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import type { CalendarClosure } from "../../useCalendarView";
// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Storefront]);

const createEditClosureFormSchema = z
  .object({
    productIds: z
      .array(z.string())
      .min(1, i18n.t("storefront:CALENDAR.CLOSURES.ERRORS.PRODUCTS")),
    name: z.string().min(1, i18n.t("storefront:CALENDAR.CLOSURES.ERRORS.NAME")),
    startISO: z
      .string()
      .min(1, i18n.t("storefront:CALENDAR.CLOSURES.ERRORS.START_DATE")),
    endISO: z
      .string()
      .min(1, i18n.t("storefront:CALENDAR.CLOSURES.ERRORS.END_DATE")),
    isDeleting: z.boolean().optional().default(false),
  })
  .superRefine((data, ctx) => {
    if (
      isAfter(
        toZonedTime(data.startISO, "UTC"),
        toZonedTime(data.endISO, "UTC"),
      )
    ) {
      ctx.addIssue({
        message: i18n.t(
          "storefront:CALENDAR.CLOSURES.ERRORS.END_DATE_AFTER_START_DATE",
        ),
        path: ["endISO"],
        code: "custom",
      });
    }
  });

export type CreateEditClosureFormValues = z.infer<
  typeof createEditClosureFormSchema
>;

interface CreateEditClosureFormOptions {
  onSuccess?: (
    v: CreateEditClosureFormValues | undefined,
    isDeleting?: boolean,
  ) => void;
  onError?: (message: string) => void;
  defaultClosure?: CalendarClosure | null;
}

const useDefaultValues = (closureId: string): CreateEditClosureFormValues => {
  const { data } = useProductClosureByIdQuery({ variables: { closureId } });

  if (!closureId)
    return {
      startISO: "",
      endISO: "",
      productIds: [],
      name: "",
      isDeleting: false,
    };

  const currentClosure = data?.productClosureById?.closure;
  const currentProductIds = data?.productClosureById?.productIds;

  return {
    startISO: currentClosure?.dateRangeISO.start
      ? formatDate(
          toZonedTime(currentClosure.dateRangeISO.start, "UTC"),
          DATE_PATTERN_SIMPLE,
        )
      : "",
    endISO: currentClosure?.dateRangeISO.end
      ? formatDate(
          toZonedTime(currentClosure.dateRangeISO.end, "UTC"),
          DATE_PATTERN_SIMPLE,
        )
      : "",
    name: currentClosure?.name ?? "",
    productIds: currentProductIds ?? [],
    isDeleting: false,
  };
};

export const useCreateEditClosureForm = ({
  onSuccess,
  onError,
  defaultClosure,
}: CreateEditClosureFormOptions) => {
  const defaultValues = useDefaultValues(defaultClosure?._id);

  const [createClosures] = useClosuresCreatePerProductMutation({
    refetchQueries: [StorefrontCalendarFeedDocument],
  });
  const [updateClosures] = useClosuresUpdatePerProductMutation({
    refetchQueries: [StorefrontCalendarFeedDocument],
  });

  return useFormHandler<CreateEditClosureFormValues>(
    useCallback(
      async data => {
        const payload = { ...data };
        const { endISO, startISO, name, productIds, isDeleting } = payload;

        try {
          if (defaultClosure?._id) {
            if ((endISO && startISO && name && productIds) || isDeleting) {
              const variables: ClosuresUpdatePerProductMutationVariables =
                isDeleting
                  ? {
                      closureId: defaultClosure?._id,
                      deleteClosure: true,
                    }
                  : endISO && startISO && name && productIds
                    ? {
                        closureId: defaultClosure._id,
                        deleteClosure: false,
                        productIds: _.compact(productIds),
                        name,
                        startIso: toZonedTime(startISO, "UTC").toISOString(),
                        endIso: toZonedTime(endISO, "UTC").toISOString(),
                      }
                    : { closureId: defaultClosure?._id, deleteClosure: false };

              const { data: res } = await updateClosures({
                variables,
              });
              if (
                res?.updateClosuresPerProduct &&
                res?.updateClosuresPerProduct.length > 0
              ) {
                onSuccess?.(data as CreateEditClosureFormValues, isDeleting);
              } else {
                throw new Error();
              }
            }
          } else {
            if (endISO && startISO && name && productIds) {
              const { data: res } = await createClosures({
                variables: {
                  endIso: toZonedTime(endISO, "UTC").toISOString(),
                  startIso: toZonedTime(startISO, "UTC").toISOString(),
                  name,
                  productIds: _.compact(productIds),
                },
              });
              if (
                res?.createClosuresPerProduct &&
                res?.createClosuresPerProduct.length > 0
              ) {
                onSuccess?.(data as CreateEditClosureFormValues);
              } else {
                throw new Error();
              }
            }
          }
        } catch {
          onError?.(
            i18n.t("storefront:CALENDAR.CLOSURES.ERRORS.FAILED_TO_SAVE"),
          );
        }
      },
      [createClosures, defaultClosure?._id, onError, onSuccess, updateClosures],
    ),
    defaultValues,
    { schema: createEditClosureFormSchema },
  );
};
